import { useMemo, useState } from 'react';

import type { GarmentInspectionView } from '@/api/inspections';
import { InspectionOutcomeValues, type InspectionOutcome } from '@/api/generated/enums';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, controlClass, textAreaClass } from '@/components/Field';
import { Button, Card, Notice, SectionTitle } from '@/components/PageHeading';
import { formatAmount } from '@core/format/money';
import {
  canSave,
  draftFrom,
  inspectionProblems,
  setCharge,
  setNote,
  setOutcome,
  ticked,
  toggle,
  total,
  toRequest,
  type ChecklistEntry,
  type InspectionDraft,
} from '@core/returns/inspection';

/**
 * What each outcome means, said on the screen rather than left to a three-word enum.
 *
 * The routing consequence is the part a clerk has to get right, and only one of the three has one
 * today: writing a garment off retires it and charges the design's replacement value. Saying so
 * beside the radio is the difference between a considered decision and a mis-click that permanently
 * takes a suit off the rail.
 */
const OUTCOME_HINTS: Record<InspectionOutcome, string> = {
  Clean: 'Back as it went out. Nothing to charge.',
  Repairable: 'Marked or torn, and worth mending. The customer is charged for what you tick below.',
  BeyondRepair:
    'Not worth mending. The garment is retired for good and the customer is charged the design’s replacement value — not the ticked lines.',
};

/**
 * Section 9's inspection, as a full page rather than a drawer.
 *
 * <p>
 * The same call `NewBookingPage` and `StockIntakePage` made, and for the reason this project keeps
 * making it: this is data entry with a customer standing there, and the checklist plus the money it
 * comes to have to be visible together. A 560px drawer would show the ticks or the total, and the
 * question being asked at the counter — "what am I paying for that?" — spans both.
 * </p>
 */
export function InspectionPage({
  view,
  saving,
  onSave,
  onBack,
  backLabel = 'Back to returns',
  saveLabel,
}: {
  view: GarmentInspectionView;
  saving: boolean;
  onSave: (payload: ReturnType<typeof toRequest>) => void;
  onBack: () => void;
  /** Where Back goes — the booking, when the walk after a take-back opened this. */
  backLabel?: string;
  /** The save button's words — "Save · next: Mojari" on the walk. */
  saveLabel?: string | undefined;
}) {
  const [draft, setDraft] = useState<InspectionDraft>(() =>
    draftFrom(view.checklist, view.inspection),
  );

  const focusRef = useAutoFocusFirstField<HTMLDivElement>();

  const problems = useMemo(
    () => inspectionProblems(draft, view.replacementValue),
    [draft, view.replacementValue],
  );

  const charged = total(draft, view.replacementValue);
  const lines = ticked(draft);
  const writeOff = draft.outcome === 'BeyondRepair';

  return (
    <div ref={focusRef} {...enterKeyScope}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3 lg:mb-6">
        <div>
          <h1 className="page-title">Inspect {view.garmentName}</h1>
          <p className="page-subtitle">
            <span className="font-mono-nums">{view.serialCode}</span>
            {view.bookingNumber && (
              <>
                {' · '}
                <span className="font-mono-nums">{view.bookingNumber}</span>
              </>
            )}
            {view.itemCategoryName && ` · ${view.itemCategoryName}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button icon="icon icon-arrow-left" shortcut="back" onClick={onBack}>
            {backLabel}
          </Button>
          <Button
            variant="primary"
            icon="icon icon-badge-check"
            shortcut="save"
            data-enter-key-commit
            loading={saving}
            disabled={saving || !canSave(draft, view.replacementValue)}
            onClick={() => onSave(toRequest(draft))}
          >
            {saveLabel ?? (view.inspection ? 'Save inspection' : 'Record inspection')}
          </Button>
        </div>
      </div>

      {problems.length > 0 && (
        <div className="mb-3 lg:mb-6">
          <Notice tone="warning" title="Before this can be saved">
            <ul className="list-none space-y-1 ps-0">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </Notice>
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-3 lg:gap-6">
        <div className="space-y-3 lg:col-span-2 lg:space-y-6">
          <Card>
            <SectionTitle>What state is it in?</SectionTitle>

            <div className="mt-3 space-y-2">
              {InspectionOutcomeValues.map((outcome) => (
                <label
                  key={outcome}
                  className="flex cursor-pointer items-start gap-3 rounded-md border border-border-color p-3 hover:bg-light"
                >
                  <input
                    type="radio"
                    name="outcome"
                    className="mt-1"
                    checked={draft.outcome === outcome}
                    onChange={() => setDraft(setOutcome(draft, outcome))}
                  />
                  <span>
                    <span className="block text-sm font-medium text-dark">
                      {outcome === 'BeyondRepair' ? 'Beyond repair' : outcome}
                    </span>
                    <span className="mt-0.5 block text-[13px] text-default">
                      {OUTCOME_HINTS[outcome]}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </Card>

          <Card>
            <SectionTitle>What is wrong with it?</SectionTitle>

            {draft.outcome === 'Clean' ? (
              <p className="mt-3 text-[13px] text-default">
                Nothing to record on a clean return. Choose another outcome to tick the checklist.
              </p>
            ) : draft.entries.length === 0 ? (
              <p className="mt-3 text-[13px] text-default">
                No kinds of damage are set up yet. Add them on the damage charge master, or record
                what you found in the note below.
              </p>
            ) : (
              <ul className="mt-3 list-none space-y-2 ps-0">
                {draft.entries.map((entry) => (
                  <ChecklistRow
                    key={entry.damageTypeId}
                    entry={entry}
                    writeOff={writeOff}
                    onToggle={() => setDraft(toggle(draft, entry.damageTypeId))}
                    onCharge={(value) => setDraft(setCharge(draft, entry.damageTypeId, value))}
                    onNote={(value) => setDraft(setNote(draft, entry.damageTypeId, value))}
                  />
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <Field
              label="Anything the checklist cannot say"
              hint="Read out to the customer with the findings, so keep it to what you would say aloud."
            >
              <textarea
                className={textAreaClass}
                rows={3}
                value={draft.notes}
                onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
              />
            </Field>
          </Card>
        </div>

        <div className="space-y-3 lg:space-y-6">
          <Card>
            <SectionTitle>What it comes to</SectionTitle>

            {writeOff ? (
              <p className="mt-3 text-[13px] text-default">
                Charged at the design’s replacement value. The ticked lines are still recorded —
                they are the evidence for the write-off — but they are not what the customer pays.
              </p>
            ) : (
              <ul className="mt-3 list-none space-y-1.5 ps-0">
                {lines.length === 0 ? (
                  <li className="text-[13px] text-default">Nothing ticked.</li>
                ) : (
                  lines.map((entry) => (
                    <li key={entry.damageTypeId} className="flex justify-between gap-3 text-[13px]">
                      <span className="text-default">{entry.name}</span>
                      <span className="font-mono-nums text-gray-900">
                        {formatAmount(entry.charge || 0)}
                      </span>
                    </li>
                  ))
                )}
              </ul>
            )}

            <div className="mt-3 flex justify-between gap-3 border-t border-border-color pt-3">
              <span className="text-sm font-medium text-dark">
                {writeOff ? 'Replacement value' : 'Damage charge'}
              </span>
              <span className="font-mono-nums text-sm font-semibold text-gray-900">
                {formatAmount(charged)}
              </span>
            </div>

            <p className="mt-2 text-[13px] text-default">
              Deducted from the deposit when the booking is settled.
            </p>
          </Card>

          <Card>
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1"
                checked={draft.acknowledgedByCustomer}
                onChange={(event) =>
                  setDraft({ ...draft, acknowledgedByCustomer: event.target.checked })
                }
              />
              <span>
                <span className="block text-sm font-medium text-dark">
                  The customer accepted this
                </span>
                <span className="mt-0.5 block text-[13px] text-default">
                  Tick it only if they agreed at the counter. Leaving it off is what tells anyone
                  reading this later that they went away unhappy.
                </span>
              </span>
            </label>
          </Card>

          {/*
            The state a clerk cannot infer from anything else on this screen. A charge over the
            shop's threshold was recorded and is deliberately *not* on the booking — so telling the
            customer the total here, with nothing said, would be quoting a figure nobody has been
            billed. Correcting the findings re-asks the question from scratch, which is why an
            approved inspection edited upwards lands back here.
          */}
          {view.inspection?.approvalState === 'Pending' && (
            <Card>
              <Notice tone="warning" title="This is waiting on a manager">
                Over the shop&rsquo;s approval threshold, so it is recorded and not billed. Until
                somebody signs for it, the deposit settles as though the garment came back clean.
              </Notice>
            </Card>
          )}

          {view.inspection?.approvalState === 'Rejected' && (
            <Card>
              <Notice tone="info" title="A manager refused this charge">
                {view.inspection.approvalNote ?? 'No reason was recorded.'}
                {view.inspection.approvedByName ? ` — ${view.inspection.approvedByName}` : ''} The
                customer has not been billed. Correcting the findings below asks again.
              </Notice>
            </Card>
          )}

          {view.inspection && (
            <Card>
              <SectionTitle>Last recorded</SectionTitle>
              <p className="mt-2 text-[13px] text-default">
                {view.inspection.inspectedByName
                  ? `By ${view.inspection.inspectedByName}.`
                  : 'By an unknown user.'}{' '}
                Saving replaces what was said — the old lines are kept, not deleted.
                {view.inspection.approvalState === 'Approved' &&
                  view.inspection.approvedByName &&
                  ` Approved by ${view.inspection.approvedByName}; editing it upwards sends it back for approval.`}
              </p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * One line of the checklist: the tick, its charge and a note.
 *
 * <p>
 * A card per line rather than a row of inputs, which is the lesson "What the tailor must do" paid
 * for: `controlClass` opens with `w-full`, so three controls side by side in a narrow column
 * collapse to stumps whose labels have already vanished with their placeholders.
 * </p>
 */
function ChecklistRow({
  entry,
  writeOff,
  onToggle,
  onCharge,
  onNote,
}: {
  entry: ChecklistEntry;
  writeOff: boolean;
  onToggle: () => void;
  onCharge: (value: number) => void;
  onNote: (value: string) => void;
}) {
  return (
    <li className="list-none rounded-md border border-border-color p-3 ps-3">
      <label className="flex cursor-pointer items-start gap-3">
        <input type="checkbox" className="mt-1" checked={entry.ticked} onChange={onToggle} />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-medium text-dark">{entry.name}</span>
            {entry.chargeId === null ? (
              <span className="text-[13px] text-warning">no published charge</span>
            ) : (
              <span className="font-mono-nums text-[13px] text-default">
                {formatAmount(entry.standardCharge)}
                {entry.isInherited && entry.resolvedFromCategoryName
                  ? ` · from ${entry.resolvedFromCategoryName}`
                  : ''}
              </span>
            )}
          </span>
          {entry.description && (
            <span className="mt-0.5 block text-[13px] text-default">{entry.description}</span>
          )}
        </span>
      </label>

      {entry.ticked && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field
            label="Charge"
            hint={writeOff ? 'Recorded as evidence; the write-off is charged instead.' : undefined}
          >
            <input
              type="number"
              min={0}
              step="0.01"
              className={controlClass}
              value={entry.charge}
              // A wheel over a number input silently rewrites it while the page is being scrolled
              // past — here that means quietly changing what a customer is charged.
              onWheel={(event) => event.currentTarget.blur()}
              onChange={(event) => onCharge(Number(event.target.value))}
            />
          </Field>

          <Field
            label="Where, and how bad"
            hint="What makes a photograph mean something a year on."
          >
            <input
              type="text"
              className={controlClass}
              value={entry.note}
              onChange={(event) => onNote(event.target.value)}
            />
          </Field>
        </div>
      )}
    </li>
  );
}
